import React from 'react';
import { X } from 'lucide-react';
import { AppSelect } from '../../../components/AppSelect';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { SettingsStatusChip } from './SettingsStatusChip';
import { SETTINGS_FIELD_LABEL, SETTINGS_TABLE_HEADER } from '../utils/settingsSectionStyles';

export type MeinshkEmployeeMapRow = {
  employeeId: string;
  name: string;
  email: string;
  personalNumber: string;
  externalId: string | null;
  externalNumber: string | null;
  matchSource: 'import' | 'auto' | 'manual' | null;
};

type RemoteEmployee = { externalId: string; name: string; email: string; employeeNumber: string };
type Suggestion = { employeeId: string; externalId: string; reason: 'email' | 'personal_number' | 'last_name' };

const REASON_LABEL: Record<Suggestion['reason'], string> = {
  email: 'E-Mail',
  personal_number: 'Personalnummer',
  last_name: 'Nachname',
};

/**
 * MP employees on the left, the MeinSHK employee they map to on the right. The MeinSHK list is
 * loaded live when the table opens; last-name matches are preselected as suggestions but only
 * saved when confirmed.
 */
export function MeinshkEmployeeMapTable({
  rows,
  canManage,
  onChanged,
}: {
  rows: MeinshkEmployeeMapRow[];
  canManage: boolean;
  onChanged: (rows: MeinshkEmployeeMapRow[]) => void;
}) {
  const [remote, setRemote] = React.useState<RemoteEmployee[] | null>(null);
  const [suggestions, setSuggestions] = React.useState<Map<string, Suggestion>>(new Map());
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    fetch('/api/settings/meinshk/employees', { credentials: 'include' })
      .then(async (res) => {
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(d?.error || 'MeinSHK-Mitarbeiter konnten nicht geladen werden.');
        if (cancelled) return;
        setRemote(Array.isArray(d.employees) ? d.employees : []);
        const map = new Map<string, Suggestion>();
        for (const s of Array.isArray(d.suggestions) ? (d.suggestions as Suggestion[]) : []) map.set(s.employeeId, s);
        setSuggestions(map);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (employeeId: string, externalId: string | null) => {
    setBusy(employeeId);
    setError(null);
    try {
      const res = await fetch('/api/settings/meinshk/employee-map', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId, externalId }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.error || 'Zuordnung konnte nicht gespeichert werden.');
      onChanged(Array.isArray(d.employeeMap) ? d.employeeMap : rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  const taken = new Set(rows.map((r) => r.externalId).filter(Boolean));
  const options = (remote ?? []).map((e) => ({
    value: e.externalId,
    label: `${e.name}${e.employeeNumber ? ` · Nr. ${e.employeeNumber}` : ''}`,
  }));
  const remoteById = new Map((remote ?? []).map((e) => [e.externalId, e]));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <h3 className={SETTINGS_FIELD_LABEL}>Mitarbeiter-Zuordnung</h3>
        <span className="text-xs text-app-text-subtle">
          {rows.filter((r) => r.externalId).length} von {rows.length} zugeordnet
        </span>
      </div>
      <p className="text-xs text-app-text-subtle">
        Zeiten, Termine und Aufgaben werden nur für zugeordnete Mitarbeiter übertragen. Gleiche E-Mail oder Personalnummer wird automatisch
        zugeordnet, ein gleicher Nachname nur vorgeschlagen. Mitarbeiter werden in MeinSHK nie angelegt.
      </p>
      {loadError ? <div className="p-3 rounded-md bg-amber-50 text-amber-800 text-sm border border-amber-200">{loadError}</div> : null}
      {error ? <div className="p-3 rounded-md bg-red-50 text-red-700 text-sm border border-red-200">{error}</div> : null}
      {remote == null && !loadError ? (
        <InlineLoadingState label="MeinSHK-Mitarbeiter werden geladen …" className="justify-start" />
      ) : (
        <div className="overflow-hidden rounded-md border border-app-border">
          <table className="w-full text-xs">
            <thead className="bg-app-surface-muted text-left">
              <tr>
                <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Mitarbeiter</th>
                <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Personalnr.</th>
                <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>MeinSHK-Mitarbeiter</th>
                <th className={`px-3 py-2 ${SETTINGS_TABLE_HEADER}`}>Quelle</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const suggestion = !row.externalId ? suggestions.get(row.employeeId) : undefined;
                const suggested = suggestion ? remoteById.get(suggestion.externalId) : undefined;
                const rowOptions = options.filter((o) => o.value === row.externalId || !taken.has(o.value));
                return (
                  <tr key={row.employeeId} className="border-t border-app-border align-middle">
                    <td className="px-3 py-2">
                      <div className="font-medium text-app-text">{row.name}</div>
                      {row.email ? <div className="text-app-text-subtle">{row.email}</div> : null}
                    </td>
                    <td className="px-3 py-2 font-mono text-app-text-muted">{row.personalNumber || '—'}</td>
                    <td className="px-3 py-2 min-w-[14rem]">
                      {canManage && remote ? (
                        <div className="flex items-center gap-1.5">
                          <AppSelect
                            size="sm"
                            value={row.externalId ?? ''}
                            onChange={(value) => void save(row.employeeId, value || null)}
                            options={[{ value: '', label: '— nicht zugeordnet —' }, ...rowOptions]}
                            disabled={busy === row.employeeId}
                            aria-label={`MeinSHK-Mitarbeiter für ${row.name}`}
                          />
                          {row.externalId ? (
                            <button
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded border border-app-border text-app-text-subtle hover:bg-app-hover hover:text-app-text"
                              title="Zuordnung entfernen"
                              disabled={busy === row.employeeId}
                              onClick={() => void save(row.employeeId, null)}
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-app-text-muted">
                          {row.externalId ? remoteById.get(row.externalId)?.name ?? `ID ${row.externalId}` : '—'}
                        </span>
                      )}
                      {suggested && canManage ? (
                        <button
                          type="button"
                          className="mt-1 text-[11px] font-medium text-pipedrive-blue hover:underline"
                          disabled={busy === row.employeeId}
                          onClick={() => void save(row.employeeId, suggested.externalId)}
                        >
                          Vorschlag übernehmen: {suggested.name} ({REASON_LABEL[suggestion!.reason]})
                        </button>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      {row.matchSource === 'manual' ? (
                        <SettingsStatusChip tone="muted">manuell</SettingsStatusChip>
                      ) : row.matchSource === 'auto' ? (
                        <SettingsStatusChip tone="ok">automatisch</SettingsStatusChip>
                      ) : suggestion ? (
                        <SettingsStatusChip tone="warn">Vorschlag</SettingsStatusChip>
                      ) : (
                        <SettingsStatusChip tone="muted">offen</SettingsStatusChip>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!rows.length ? (
                <tr>
                  <td colSpan={4} className="px-3 py-4 text-center text-app-text-subtle">
                    Keine aktiven Mitarbeiter.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
